import React from "react";
import { cn } from "@/lib/utils";

/**
 * ProgressRing — circular progress primitive.
 *
 * Uses two concentric SVG circles: the track (surface-inset) and the
 * fill (accent, drawn with `stroke-dasharray` + `stroke-dashoffset`).
 * Renders an accessible progressbar role with numeric slot in the
 * middle if `showValue` is true.
 *
 * Props:
 *   • value        — 0..max (clamped)
 *   • max          — default 100
 *   • size         — pixel diameter (default 96)
 *   • strokeWidth  — default 8
 *   • tone         — accent | success | warning | danger
 *   • showValue    — center numeric readout (default true)
 *   • label        — accessible label (aria-label fallback)
 */

const TONE_STROKE = {
  accent:  "var(--ds-color-accent)",
  success: "var(--ds-color-success)",
  warning: "var(--ds-color-warning)",
  danger:  "var(--ds-color-danger)",
};

export function ProgressRing({
  value = 0,
  max = 100,
  size = 96,
  strokeWidth = 8,
  tone = "accent",
  showValue = true,
  label,
  className,
  "data-testid": testId,
  ...rest
}) {
  const safeMax = max > 0 ? max : 100;
  const clamped = Math.min(Math.max(Number(value) || 0, 0), safeMax);
  const pct = clamped / safeMax;

  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - pct);
  const stroke = TONE_STROKE[tone] || TONE_STROKE.accent;

  return (
    <div
      className={cn("relative inline-flex items-center justify-center", className)}
      style={{ width: size, height: size }}
      data-testid={testId}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={safeMax}
      aria-valuenow={clamped}
      aria-label={label || "Progress"}
      {...rest}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--ds-color-surface-inset)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{
            transition: `stroke-dashoffset var(--ds-duration-normal) var(--ds-easing-standard)`,
          }}
        />
      </svg>
      {showValue && (
        <span className="absolute font-semibold tabular-nums text-[var(--ds-color-ink)]" style={{ fontSize: Math.max(12, Math.floor(size * 0.22)) }}>
          {Math.round(pct * 100)}
        </span>
      )}
    </div>
  );
}

export default ProgressRing;
